import {useEffect, useRef, useState} from 'react';
import {kit} from './design';
import {fireCustomEvent} from '../util/Analytics';

interface Props {
  visible: boolean;
  ready: boolean;
}

// Same asset TopNav uses for the real wordmark -- see
// common/design/TopNav.tsx LOGO_SRC.
const LOGO_SRC = 'https://cdn.bidmii.com/media/web_assets/logo.webp';

// Production cold starts measured at 41-49s (Java/Spring boot). Fill to 90%
// over 50s so the bar is still visibly moving when the server finally answers,
// instead of racing to the end and freezing.
const FILL_DURATION = 50000;

export default function ColdStartOverlay({visible, ready}: Props) {
  const [progress, setProgress] = useState(0);
  const [fading, setFading] = useState(false);
  const [gone, setGone] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const shownEventFiredRef = useRef(false);

  // Record that a visitor actually saw this waiting screen (i.e. the server
  // took longer than the 2s timer in App.tsx) -- exactly once per page load.
  // Prefer the early gtag loaded by public/index.html, which is ready even
  // when Firebase Analytics hasn't initialized yet; fall back to the Firebase
  // helper otherwise. Measurement only -- no behaviour/timing change.
  useEffect(() => {
    if (!visible || shownEventFiredRef.current) return;
    shownEventFiredRef.current = true;
    const gtag = (window as $TSFixMe).gtag;
    if (typeof gtag === 'function') {
      gtag('event', 'cold_start_overlay_shown');
    } else {
      fireCustomEvent('cold_start_overlay_shown');
    }
  }, [visible]);

  // Animate progress bar 0 → 90% over 50s while overlay is showing
  useEffect(() => {
    if (!visible) return;
    const step = (now: number) => {
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const pct = Math.min(90, (elapsed / FILL_DURATION) * 90);
      setProgress(pct);
      if (pct < 90) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visible]);

  // When server responds: jump bar to 100%, then fade out and unmount
  useEffect(() => {
    if (!ready) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setProgress(100);
    const fadeTimer = setTimeout(() => setFading(true), 400);
    const goneTimer = setTimeout(() => setGone(true), 1100);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(goneTimer);
    };
  }, [ready]);

  if (!visible || gone) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.6s ease-out',
        pointerEvents: fading ? 'none' : 'auto',
      }}>
      <img src={LOGO_SRC} alt="Bidmii" style={{height: 28, display: 'block'}} />
      <p
        style={{
          fontFamily:
            "'AeonikProReqular', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: 15,
          fontWeight: 600,
          color: kit.ink,
          margin: 0,
        }}>
        Just a moment...
      </p>
      <p
        style={{
          fontFamily:
            "'AeonikProReqular', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: 13,
          color: kit.slate,
          margin: 0,
          maxWidth: 260,
          textAlign: 'center',
          lineHeight: 1.5,
        }}>
        Bidmii is waking up. This can take up to a minute the first time. Thanks
        for waiting!
      </p>
      <div
        style={{
          width: 200,
          height: 3,
          background: kit.line,
          borderRadius: 2,
          overflow: 'hidden',
        }}>
        <div
          style={{
            height: '100%',
            width: `${progress}%`,
            background: kit.blue,
            borderRadius: 2,
            transition: ready ? 'width 0.3s ease-out' : undefined,
          }}
        />
      </div>
    </div>
  );
}
